import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SupplyManagementPanel = ({ supplies, loading, onUpdate }) => {
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState(null);

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading supply data...</div>;
  }

  if (!supplies) return null;

  const handleReorder = (supplyType) => {
    setSelectedSupply(supplyType);
    setShowReorderModal(true);
  };

  const getSupplyStatus = (level, threshold) => {
    if (level <= threshold) return { color: 'red', status: 'Critical' };
    if (level <= threshold * 2) return { color: 'yellow', status: 'Low' };
    return { color: 'green', status: 'Good' };
  };

  const ribbonStatus = getSupplyStatus(supplies?.ribbon?.level, supplies?.ribbon?.reorderThreshold);
  const cardStatus = getSupplyStatus(supplies?.cardStock?.level, supplies?.cardStock?.reorderThreshold);
  const cleaningStatus = getSupplyStatus(supplies?.cleaningKit?.level, supplies?.cleaningKit?.reorderThreshold);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Supply Management</h2>
        <p className="text-sm text-gray-600 mt-1">
          Track inventory levels and manage reordering for printer supplies
        </p>
      </div>
      {/* Supply Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Ribbon Supply */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Icon name="Droplet" className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Ribbon</h3>
                <p className="text-xs text-gray-600">{supplies?.ribbon?.type}</p>
              </div>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium bg-${ribbonStatus?.color}-100 text-${ribbonStatus?.color}-800`}>
              {ribbonStatus?.status}
            </span>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Level</span>
              <span className="text-sm font-semibold text-gray-900">{supplies?.ribbon?.level}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className={`bg-${ribbonStatus?.color}-600 h-3 rounded-full transition-all`}
                style={{ width: `${supplies?.ribbon?.level}%` }}
              ></div>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Estimated Prints</span>
              <span className="font-medium text-gray-900">{supplies?.ribbon?.estimatedPrints}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Reorder Threshold</span>
              <span className="font-medium text-gray-900">{supplies?.ribbon?.reorderThreshold}%</span>
            </div>
          </div>

          <button
            onClick={() => handleReorder('ribbon')}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Icon name="ShoppingCart" className="w-4 h-4" />
            Reorder Ribbon
          </button>
        </div>

        {/* Card Stock */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Icon name="CreditCard" className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Card Stock</h3>
                <p className="text-xs text-gray-600">{supplies?.cardStock?.type}</p>
              </div>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium bg-${cardStatus?.color}-100 text-${cardStatus?.color}-800`}>
              {cardStatus?.status}
            </span>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Quantity</span>
              <span className="text-sm font-semibold text-gray-900">{supplies?.cardStock?.level} cards</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className={`bg-${cardStatus?.color}-600 h-3 rounded-full transition-all`}
                style={{ width: `${(supplies?.cardStock?.level / 200) * 100}%` }}
              ></div>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Reorder Threshold</span>
              <span className="font-medium text-gray-900">{supplies?.cardStock?.reorderThreshold} cards</span>
            </div>
          </div>

          <button
            onClick={() => handleReorder('cardStock')}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Icon name="ShoppingCart" className="w-4 h-4" />
            Reorder Cards
          </button>
        </div>

        {/* Cleaning Kit */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Icon name="Sparkles" className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Cleaning Kit</h3>
                <p className="text-xs text-gray-600">Maintenance Supplies</p>
              </div>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium bg-${cleaningStatus?.color}-100 text-${cleaningStatus?.color}-800`}>
              {cleaningStatus?.status}
            </span>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Quantity</span>
              <span className="text-sm font-semibold text-gray-900">{supplies?.cleaningKit?.level} kits</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className={`bg-${cleaningStatus?.color}-600 h-3 rounded-full transition-all`}
                style={{ width: `${(supplies?.cleaningKit?.level / 5) * 100}%` }}
              ></div>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Last Used</span>
              <span className="font-medium text-gray-900">
                {new Date(supplies?.cleaningKit?.lastUsed)?.toLocaleDateString()}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Reorder Threshold</span>
              <span className="font-medium text-gray-900">{supplies?.cleaningKit?.reorderThreshold} kits</span>
            </div>
          </div>

          <button
            onClick={() => handleReorder('cleaningKit')}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Icon name="ShoppingCart" className="w-4 h-4" />
            Reorder Kit
          </button>
        </div>
      </div>
      {/* Reorder Alert */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
        <Icon name="AlertTriangle" className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-orange-900">Automatic Reorder Alerts</h3>
          <p className="text-orange-700 text-sm">
            System will automatically notify when supplies reach reorder thresholds. Configure vendor integration for automated ordering.
          </p>
        </div>
      </div>
      {/* Reorder Modal */}
      {showReorderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Reorder {selectedSupply}</h3>
            <p className="text-gray-600 mb-6">
              This feature will integrate with your vendor management system to place automatic reorders.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowReorderModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  alert(`Reorder request submitted for ${selectedSupply}`);
                  setShowReorderModal(false);
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Confirm Reorder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplyManagementPanel;
